/**
 * Client Dashboard
 *
 * Main dashboard for clients to view their delivery progress.
 * Answers at a glance: What they bought, what they received, what's next.
 *
 * PRINCIPLES:
 * - Read-only, informational view
 * - No internal system terms exposed
 * - Trust-building, reassuring presentation
 * - Uses existing contexts (ClientLedger, Agent, Contracts)
 * - Aggregates ALL contracts for multi-contract agents
 */

import { useMemo, useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Users, Shield, FileText, Settings, ChevronRight, PauseCircle, CreditCard } from "lucide-react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { DeliveryOverviewCard, type DeliveryStatus } from "@/components/dashboard/DeliveryOverviewCard";
import { PlanSummaryCard, type PlanType } from "@/components/dashboard/PlanSummaryCard";
import { DeliveryStatusMessage } from "@/components/dashboard/DeliveryStatusMessage";
import { ClientActionsCard } from "@/components/dashboard/ClientActionsCard";
import { RecentActivityFeed, type ActivityItem } from "@/components/dashboard/RecentActivityFeed";
import { useAgent } from "@/contexts/AgentContext";
import { useContracts, computeContractMetrics } from "@/contexts/ContractsContext";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { toast } from "sonner";

// Map internal delivery status to client-friendly status.
// urgencyScore is now pace-deviation based: 0 = on/ahead of schedule,
// 0.5 = need 1.5x original pace, 1.0 = need 2x+ original pace.
function mapToClientStatus(
  progressPercent: number,
  urgencyScore: number,
  daysRemaining: number,
  leadsRemaining: number
): DeliveryStatus {
  // Complete
  if (leadsRemaining <= 0) {
    return progressPercent > 100 ? 'OVER_DELIVERED' : 'COMPLETE';
  }

  // At risk: needs > 1.5x the original pace, or cycle almost over with many leads left
  if (urgencyScore > 0.5 || (daysRemaining <= 3 && leadsRemaining > daysRemaining * 5)) {
    return 'AT_RISK';
  }

  // Slightly behind: needs 1.2x–1.5x the original pace
  if (urgencyScore > 0.2) {
    return 'SLIGHTLY_BEHIND';
  }

  // On track
  return 'ON_TRACK';
}

// Extract display name from lead_profile JSON
function getLeadDisplayName(profile: Record<string, unknown> | null): string {
  if (!profile) return "New Lead";
  const first = String(profile.first_name || "").trim();
  const last = String(profile.last_name || "").trim();
  if (first || last) return `${first} ${last}`.trim();
  return "New Lead";
}

const Dashboard = () => {
  const { state: agentState } = useAgent();
  const { state: contractsState } = useContracts();
  const { user, signOut } = useAuth();
  const { isAdmin, role, loading: adminLoading } = useAdmin();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const viewAsAgentId = searchParams.get("viewAs");
  const isViewingAs = role === "admin" && !!viewAsAgentId;
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  // Check if agent has completed onboarding (has licensed_states set).
  // Admin/sales users without an agent profile get a demo account seeded automatically.
  // Phase D: Skip entirely when admin is viewing as a client.
  useEffect(() => {
    if (isViewingAs) {
      setOnboardingChecked(true);
      return;
    }
    if (!user || adminLoading) return;

    const checkOnboarding = async () => {
      try {
        console.log("[Dashboard] Checking onboarding for user.id:", user.id, "user.email:", user.email);

        // Anti-loop guard: if we've already redirected to onboarding recently,
        // stop after 3 redirects within a 5-minute window.
        const redirectRaw = sessionStorage.getItem("tj_onboarding_redirects");
        let redirectData: { count: number; ts: number } = { count: 0, ts: 0 };
        try {
          if (redirectRaw) redirectData = JSON.parse(redirectRaw);
        } catch { /* corrupted data — start fresh */ }

        // Auto-expire stale redirect counters (older than 5 minutes)
        const REDIRECT_TTL_MS = 5 * 60 * 1000;
        if (Date.now() - redirectData.ts > REDIRECT_TTL_MS) {
          redirectData = { count: 0, ts: 0 };
        }
        const onboardingRedirectCount = redirectData.count;

        // Use SECURITY DEFINER RPC to resolve agent identity.
        // This handles the case where the agent was created (e.g. by webhook) with
        // a different ID than auth.uid(). RLS blocks all client-side fallback queries,
        // so only a server-side function can find and reconcile the mismatch.
        const { data: resolved, error } = await (supabase.rpc as any)(
          "resolve_agent_identity"
        );

        console.log("[Dashboard] resolve_agent_identity result:", { resolved, error });

        let needsOnboarding = true;

        if (!error && resolved && !resolved.error) {
          const resolvedBy = resolved.resolved_by;

          if (resolvedBy === 'id' || resolvedBy === 'email_reconciled') {
            const licensedStates = resolved.licensed_states;
            if (Array.isArray(licensedStates) && licensedStates.length > 0) {
              needsOnboarding = false;
            }

            if (resolvedBy === 'email_reconciled') {
              console.log("[Dashboard] Agent ID reconciled from", resolved.old_id, "to", resolved.id);
            }
          }
          // 'not_found' → needsOnboarding stays true
        } else {
          // RPC not yet deployed or failed — fallback to direct SELECT.
          // This only works when agent.id already matches auth.uid() (no ID mismatch),
          // but it prevents a regression where onboarded agents get stuck in a loop
          // just because the migration hasn't been applied yet.
          console.warn("[Dashboard] RPC resolve_agent_identity failed, falling back to direct SELECT:", error);
          const { data: fallbackAgent } = await (supabase
            .from("agents")
            .select("id, licensed_states, email, is_demo")
            .eq("id", user.id)
            .maybeSingle() as any);

          if (fallbackAgent?.licensed_states && (fallbackAgent.licensed_states as string[]).length > 0) {
            needsOnboarding = false;
            console.log("[Dashboard] Fallback SELECT found onboarded agent:", fallbackAgent.id);
          }
        }

        // Check if the agent's account is deactivated (is_active = false).
        // If so, sign out and redirect to login with a message.
        if (!needsOnboarding && !isAdmin) {
          const { data: activeCheck } = await supabase
            .from("agents")
            .select("is_active")
            .eq("id", user.id)
            .maybeSingle();

          if (activeCheck && activeCheck.is_active === false) {
            console.log("[Dashboard] Account deactivated, signing out user:", user.id);
            toast.error("Your account has been deactivated. Contact your account manager to reactivate.");
            await signOut();
            return;
          }
        }

        console.log("[Dashboard] needsOnboarding:", needsOnboarding, "isAdmin:", isAdmin);

        if (needsOnboarding) {
          // Admin/sales users: auto-seed demo account instead of redirecting to onboarding
          if (isAdmin) {
            console.log("[Dashboard] Admin user without agent profile — seeding demo account");
            const { error: seedError } = await (supabase.rpc as any)("seed_demo_account", {
              p_user_id: user.id,
              p_email: user.email || "",
              p_name: user.email?.split("@")[0] || "Demo Agent",
            });

            if (seedError) {
              console.error("[Dashboard] Failed to seed demo account:", seedError);
              // Fall through — show empty dashboard rather than redirect admin to onboarding
            } else {
              console.log("[Dashboard] Demo account seeded, reloading page");
              window.location.reload();
              return;
            }
          } else {
            // Anti-loop: if we've redirected more than 2 times in this tab session,
            // stop redirecting and show the dashboard (empty state is better than infinite loop)
            if (onboardingRedirectCount >= 3) {
              console.error(
                "[Dashboard] LOOP DETECTED: Already redirected to onboarding",
                onboardingRedirectCount,
                "times. Stopping redirect to prevent infinite loop.",
                "Resolved:", resolved, "Error:", error
              );
              // Don't remove counter — keep it to prevent restart
              toast.error("We couldn't load your profile. Please sign out and sign in again.");
              setOnboardingChecked(true);
              return;
            }

            sessionStorage.setItem(
              "tj_onboarding_redirects",
              JSON.stringify({ count: onboardingRedirectCount + 1, ts: Date.now() })
            );

            // Regular agent: redirect to onboarding
            const { data: proposalData } = await (supabase
              .from("contract_proposals" as any)
              .select("id")
              .eq("agent_email", user.email)
              .order("created_at", { ascending: false })
              .limit(1)
              .maybeSingle() as any);

            const url = proposalData?.id
              ? `/onboarding?proposal_id=${proposalData.id}`
              : "/onboarding";
            console.log("[Dashboard] Redirecting to onboarding:", url, "(redirect #" + (onboardingRedirectCount + 1) + ")");
            navigate(url, { replace: true });
            return;
          }
        }

        // Successfully loaded — only clear counter if TTL has expired (prevents
        // reset-then-fail loops where an intermittent RPC success resets the guard)
        if (Date.now() - redirectData.ts > REDIRECT_TTL_MS) {
          sessionStorage.removeItem("tj_onboarding_redirects");
        }
        console.log("[Dashboard] Onboarding complete, showing dashboard");
        setOnboardingChecked(true);
      } catch (err) {
        console.error("[Dashboard] Onboarding check failed:", err);
        if (!isAdmin) {
          navigate("/onboarding", { replace: true });
        } else {
          setOnboardingChecked(true);
        }
      }
    };

    checkOnboarding();
  }, [user, navigate, isAdmin, adminLoading, isViewingAs]);

  // Get ALL active contracts for this agent (not just the first one)
  const agentId = agentState.profile.id;
  const agentEmail = agentState.profile.email;

  const allActiveContracts = useMemo(() => {
    return contractsState.contracts.filter(
      (c) => (c.status === 'active' || c.status === 'at_risk') &&
             (c.agentId === agentId || (agentEmail && c.agentEmail === agentEmail))
    );
  }, [contractsState.contracts, agentId, agentEmail]);

  // Detect paused contracts (payment failure, agent-requested, or admin-requested)
  const pausedContracts = useMemo(() => {
    return contractsState.contracts.filter(
      (c) => c.status === 'paused' &&
             (c.agentId === agentId || (agentEmail && c.agentEmail === agentEmail))
    );
  }, [contractsState.contracts, agentId, agentEmail]);

  const hasPausedContracts = pausedContracts.length > 0 && allActiveContracts.length === 0;

  // Compute aggregated metrics across all active contracts
  // NOTE: We aggregate directly from allActiveContracts (which matches by agentId OR agentEmail)
  // instead of relying on the ClientLedger, which only groups by agentId and can miss
  // contracts where agent_id hasn't been claimed yet (e.g. webhook-created contracts).
  const aggregatedData = useMemo(() => {
    if (allActiveContracts.length === 0) return null;

    const totalPurchased = allActiveContracts.reduce((sum, c) => sum + c.totalLeadsCommitted, 0);
    const totalDelivered = allActiveContracts.reduce((sum, c) => sum + c.leadsDelivered, 0);
    const leadsRemaining = Math.max(0, totalPurchased - totalDelivered);

    // Compute weighted urgency and aggregated days remaining
    let weightedUrgency = 0;
    let minDaysRemaining = Infinity;
    let earliestStart = Infinity;
    let latestEnd = -Infinity;

    for (const contract of allActiveContracts) {
      const metrics = computeContractMetrics(contract);
      weightedUrgency += metrics.urgencyScore * contract.totalLeadsCommitted;
      if (metrics.daysRemaining < minDaysRemaining) {
        minDaysRemaining = metrics.daysRemaining;
      }
      if (contract.cycleStart < earliestStart) {
        earliestStart = contract.cycleStart;
      }
      if (contract.cycleEnd > latestEnd) {
        latestEnd = contract.cycleEnd;
      }
    }

    const avgUrgency = totalPurchased > 0 ? weightedUrgency / totalPurchased : 0;
    const progressPercent = totalPurchased > 0 ? (totalDelivered / totalPurchased) * 100 : 0;
    const daysRemaining = minDaysRemaining === Infinity ? 0 : minDaysRemaining;

    const status = mapToClientStatus(progressPercent, avgUrgency, daysRemaining, leadsRemaining);

    // Determine plan type — use the first contract's type or the most common
    const planType = allActiveContracts[0]?.contractType as PlanType;

    return {
      totalPurchased,
      totalDelivered,
      remainingLeads: leadsRemaining,
      status,
      daysRemaining,
      estimatedCompletionDate: latestEnd > 0 ? format(new Date(latestEnd), 'MMM d, yyyy') : undefined,
      planType,
      startDate: earliestStart < Infinity ? new Date(earliestStart) : new Date(),
      endDate: latestEnd > 0 ? new Date(latestEnd) : new Date(),
      hasMultipleContracts: allActiveContracts.length > 1,
    };
  }, [allActiveContracts]);

  const [recentActivities, setRecentActivities] = useState<ActivityItem[]>([]);

  useEffect(() => {
    if (!agentId) return;

    const fetchRecentActivity = async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("id, lead_type_name, status, created_at, lead_profile")
        .eq("routed_to_agent_id", agentId)
        .in("status", ["ROUTED", "ARCHIVED", "CREDITED"])
        .order("created_at", { ascending: false })
        .limit(5);

      if (error) {
        console.error("[Dashboard] Failed to fetch recent activity:", error);
        return;
      }

      if (!data || data.length === 0) return;

      const activities: ActivityItem[] = data.map((row) => ({
        id: row.id,
        type: "lead_delivered" as const,
        title: `${getLeadDisplayName(row.lead_profile as Record<string, unknown> | null)} assigned`,
        description: row.lead_type_name,
        timestamp: new Date(row.created_at),
      }));

      setRecentActivities(activities);
    };

    fetchRecentActivity();
  }, [agentId]);

  if (!onboardingChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {aggregatedData ? (
          <>
            {/* Delivery Overview - Primary Card (now aggregated across ALL contracts) */}
            <DeliveryOverviewCard
              totalPurchased={aggregatedData.totalPurchased}
              totalDelivered={aggregatedData.totalDelivered}
              remainingLeads={aggregatedData.remainingLeads}
              status={aggregatedData.status}
              daysRemaining={aggregatedData.daysRemaining}
              estimatedCompletionDate={aggregatedData.estimatedCompletionDate}
            />

            {/* Status Message */}
            <DeliveryStatusMessage
              status={aggregatedData.status}
              daysRemaining={aggregatedData.daysRemaining}
              remainingLeads={aggregatedData.remainingLeads}
            />

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Plan Summary — shows aggregated totals */}
                <PlanSummaryCard
                  planType={aggregatedData.planType}
                  totalLeads={aggregatedData.totalPurchased}
                  startDate={aggregatedData.startDate}
                  endDate={aggregatedData.endDate}
                  remainingLeads={aggregatedData.remainingLeads}
                  isActive={true}
                />

                {/* Quick Actions */}
                <ClientActionsCard status={aggregatedData.status} />
              </div>

              {/* Right Column */}
              <div>
                {/* Recent Activity */}
                <RecentActivityFeed activities={recentActivities} />
              </div>
            </div>
          </>
        ) : hasPausedContracts ? (
          /* Paused contracts — reason-aware messaging */
          (() => {
            const reason = pausedContracts[0]?.pausedReason;
            const isPaymentFailure = !reason || reason === "payment_failed";
            const isAgentRequested = reason === "agent_requested";
            const borderColor = isPaymentFailure ? "border-red-200 dark:border-red-900" : "border-amber-200 dark:border-amber-900";
            const bgColor = isPaymentFailure ? "bg-red-50 dark:bg-red-950/20" : "bg-amber-50 dark:bg-amber-950/20";
            const iconColor = isPaymentFailure ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400";
            const titleColor = isPaymentFailure ? "text-red-800 dark:text-red-300" : "text-amber-800 dark:text-amber-300";
            const descColor = isPaymentFailure ? "text-red-700 dark:text-red-400" : "text-amber-700 dark:text-amber-400";
            return (
              <div className="space-y-6">
                <div className={`rounded-2xl border ${borderColor} ${bgColor} p-8 text-center`}>
                  <PauseCircle className={`w-10 h-10 ${iconColor} mx-auto mb-3`} />
                  <h2 className={`text-xl font-semibold ${titleColor} mb-2`}>
                    {isPaymentFailure ? "Payment Issue — Delivery Paused" : "Your Delivery is Paused"}
                  </h2>
                  <p className={`text-sm ${descColor} mb-4`}>
                    {isPaymentFailure
                      ? "A recent payment failed. Please update your payment method to resume lead delivery."
                      : isAgentRequested
                        ? "You paused your lead delivery. Resume anytime from your Wallet page. Auto-resumes after 14 days."
                        : "Your delivery has been paused by your account manager. Contact support if you have questions."}
                  </p>
                  <Link to="/dashboard/wallet">
                    <Button className="gap-2">
                      {isPaymentFailure ? (
                        <><CreditCard className="w-4 h-4" /> Go to Billing</>
                      ) : (
                        <><PauseCircle className="w-4 h-4" /> Go to Wallet</>
                      )}
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })()
        ) : (
          /* No active contract — show welcome state with quick-start cards */
          <div className="space-y-6">
            <div className="rounded-2xl border bg-card p-8 text-center">
              <h2 className="text-xl font-semibold text-foreground mb-2">Welcome, {agentState.profile.firstName}!</h2>
              <p className="text-muted-foreground">
                Your account is set up. While your contract is being activated, explore the resources below.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  title: "How Leads Work",
                  description: "See how leads are matched and delivered to you",
                  href: "/dashboard/leads",
                  icon: Users,
                },
                {
                  title: "Set Up Delivery Preferences",
                  description: "Configure your daily cap and lead type preferences",
                  href: "/dashboard/delivery-settings",
                  icon: Shield,
                },
                {
                  title: "Contract & Billing",
                  description: "Manage your subscription, billing, and delivery progress",
                  href: "/dashboard/wallet",
                  icon: FileText,
                },
                {
                  title: "Update License Info",
                  description: "Verify your licensed states and profile details",
                  href: "/dashboard/settings",
                  icon: Settings,
                },
              ].map((card) => (
                <Link
                  key={card.href}
                  to={card.href}
                  className="flex items-center gap-4 p-5 rounded-2xl border bg-card hover:bg-muted/50 transition-colors group"
                >
                  <div className="w-11 h-11 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <card.icon className="w-5 h-5 text-accent" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground">{card.title}</p>
                    <p className="text-sm text-muted-foreground">{card.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
